import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  ArrowRight,
  Shield,
  Clock,
  MapPin,
  CheckCircle2,
  Droplet,
  Star,
  Wrench,
  Zap,
  Award,
  ThumbsUp,
  AlertTriangle,
  Calendar,
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { SEO } from '../components/shared/SEO';
import { SERVICES, SERVICE_AREAS, COMPANY_INFO } from '../data/mock';

// ── Real uploaded photos — no text overlays ──────────────────────────────────
const PHOTOS = {
  hero: '/sprinklers.webp',
  weFixIt: '/sprinkler-system.webp',
  tceqLicense: '/tceq-license.webp',
};

// Map service IDs to real photos — each photo used in exactly one place
const SERVICE_PHOTO_MAP = {
  'sprinkler-repair': '/irrigation-rotors.webp',
  'sprinkler-leak-repair': '/pipe-repair.webp',
  'backflow-testing': '/backflow-rpz.webp',
  'valve-repair': '/leak-repair.webp',
  'sprinkler-head-repair': '/broken-head.webp',
  'controller-repair': '/controller-repair.webp',
  'commercial-irrigation': '/commercial-repair.webp',
  'hoa-irrigation': '/sprinkler-system.webp',
  'emergency-repair': '/broken-head.webp',
  'pressure-testing': '/pressure-testing.webp',
  'sprinkler-winterization': '/backflow-rpz.webp',
};

const TESTIMONIALS = [
  {
    name: 'David H.',
    city: 'Plano',
    source: 'Google',
    stars: 5,
    text: 'These guys are the real deal. Called Monday morning, they were at my house by noon. Found a broken valve and two bad heads, fixed everything in one visit. Price was fair and exactly what they quoted. Highly recommend.',
  },
  {
    name: 'Melissa R.',
    city: 'Allen',
    source: 'Google',
    stars: 5,
    text: 'My water bill was through the roof. AAA tracked down an underground leak I had no idea about in under 30 minutes. Professional, licensed, and completely transparent about pricing. I won\'t use anyone else.',
  },
  {
    name: 'Tom B.',
    city: 'Frisco',
    source: 'Yelp',
    stars: 5,
    text: 'We manage a commercial office park and have been using AAA for quarterly maintenance for two years. Zero unexpected breakdowns since we started. They keep detailed records and show up when they say they will.',
  },
  {
    name: 'Angela W.',
    city: 'McKinney',
    source: 'Google',
    stars: 5,
    text: 'Controller stopped working right before a heat wave. AAA came out same day, diagnosed a wiring issue, and had everything running in an hour. The tech explained everything clearly. Outstanding service.',
  },
  {
    name: 'Robert S.',
    city: 'Richardson',
    source: 'Yelp',
    stars: 5,
    text: 'Had three companies look at my system. AAA was the only one who actually found the root cause instead of just replacing parts and guessing. Fixed it right the first time. That is integrity.',
  },
];

export default function HomePage() {
  const FEATURED_IDS = ['sprinkler-repair', 'valve-repair', 'controller-repair', 'pressure-testing', 'commercial-irrigation', 'emergency-repair'];
  const featuredServices = FEATURED_IDS.map(id => SERVICES.find(s => s.id === id)).filter(Boolean);

  return (
    <Layout>
      <SEO
        title="Sprinkler Repair Allen TX | AAA Irrigation Service"
        description="TCEQ Licensed sprinkler repair in Allen, Plano, Frisco &amp; McKinney TX. Irrigation repair, backflow installation &amp; commercial service. Call 469-751-3567."
        keywords="sprinkler repair Allen TX, irrigation repair Allen TX, sprinkler repair Plano TX, sprinkler repair Frisco TX, irrigation repair McKinney TX, sprinkler system repair near me, backflow installation Collin County, TCEQ licensed irrigator, commercial irrigation DFW"
        canonical="https://aaairrigationservice.com/"
      />

      {/* ═══════════════════════════════════════════════════════════ HERO */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Full-bleed background photo */}
        <div className="absolute inset-0">
          <img
            src={PHOTOS.hero}
            srcSet="/sprinklers-480.webp 480w, /sprinklers-768.webp 768w, /sprinklers.webp 1000w"
            sizes="100vw"
            alt="Three rotor sprinkler heads spraying water over a green lawn"
            className="w-full h-full object-cover"
            fetchpriority="high"
            width="1000"
            height="665"
          />
          {/* Dark gradient overlay — no text written on the photo itself */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/90 via-[#0f172a]/70 to-[#0f172a]/30" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">

            {/* TCEQ badge */}
            <div className="inline-flex items-center gap-2 bg-[#059669]/20 border border-[#059669]/40 rounded-full px-4 py-1.5 mb-6">
              <Shield className="w-4 h-4 text-[#059669]" />
              <span className="text-sm font-semibold text-[#059669]">
                TCEQ License #{COMPANY_INFO.license}
              </span>
            </div>

            {/* H1 — search-optimized, brand-styled */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black italic uppercase text-white leading-none mb-4 tracking-tight">
              North Texas<br />
              <span className="text-[#1b7abf]">Sprinkler</span><br />
              Repair Experts
            </h1>

            <p className="text-xl text-slate-200 mb-4 font-medium leading-relaxed">
              Emergency service · Fully stocked trucks · Fixed on the first visit.<br />
              <span className="text-white font-bold">
                Serving Plano, Allen, Frisco &amp; all of Collin County since 2005.
              </span>
            </p>

            {/* Star rating inline */}
            <div className="flex items-center gap-2 mb-8">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span className="text-white font-bold">4.9</span>
              <span className="text-slate-300 text-sm">· 29 Reviews</span>
            </div>

            {/* Primary CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${COMPANY_INFO.phone}`}
                className="inline-flex items-center justify-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-black text-xl px-8 py-5 rounded-2xl shadow-2xl shadow-orange-600/40 hover:shadow-orange-600/60 transition-all duration-300 hover:scale-105 uppercase tracking-wide"
              >
                <Phone className="w-6 h-6" />
                {COMPANY_INFO.phoneFormatted}
              </a>
              <Link
                to="/schedule"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/50 text-white font-bold text-lg px-8 py-5 rounded-2xl transition-all duration-300"
              >
                <Calendar className="w-5 h-5" />
                Schedule Online
              </Link>
            </div>

            {/* Quick trust pills */}
            <div className="flex flex-wrap gap-4 mt-8 text-sm text-slate-300">
              {[
                'Licensed & Insured',
                'Emergency Service',
                '1-Year Warranty',
                'No Surprise Pricing',
              ].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#059669]" />
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════ TRUST BAR */}
      <section className="bg-[#1b7abf] py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-white">
            {[
              { value: '4.9★', label: '29 Reviews' },
              { value: '20+', label: 'Years in Business' },
              { value: '1-Year', label: 'Parts & Labor Warranty' },
              { value: '100%', label: 'First-Visit Fix Goal' },
            ].map((stat, i) => (
              <div key={i} className="py-2">
                <div className="text-2xl md:text-3xl font-black">{stat.value}</div>
                <div className="text-white text-sm font-semibold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════ FIRST VISIT FIX SECTION */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Photo side — clean photo, no text overlaid */}
            <div className="relative">
              <img
                src={PHOTOS.weFixIt}
                alt="AAA Irrigation Service technician performing sprinkler repair"
                className="rounded-3xl shadow-2xl w-full h-[480px] object-cover"
                loading="lazy"
                width="640"
                height="480"
              />
            </div>

            {/* Content side */}
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-1.5 mb-6">
                <Wrench className="w-4 h-4 text-[#c2410c]" />
                <span className="text-sm font-bold text-[#c2410c] uppercase tracking-wide">
                  Our #1 Promise
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black italic uppercase text-slate-900 mb-6 leading-tight">
                We Fix It<br />
                <span className="text-[#1b7abf]">On The First Visit</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Our trucks are fully stocked with the most common parts for every major brand — Rain
                Bird, Hunter, Toro, Irritrol, Rachio, and Irrigreen. No waiting for parts. No second
                trips. <strong>We show up ready to solve your problem.</strong>
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Fully stocked service trucks — we carry the parts',
                  'TCEQ licensed technicians on every job',
                  'All repairs priced and approved before we start',
                  'Digital signature required before any work begins',
                  'Factory certified on Rachio and Irrigreen systems',
                  '1-year warranty on all parts and labor',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#059669] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href={`tel:${COMPANY_INFO.phone}`}
                className="inline-flex items-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-black text-lg px-8 py-4 rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300 uppercase tracking-wide"
              >
                <Phone className="w-5 h-5" />
                Call {COMPANY_INFO.phoneFormatted}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════ EMERGENCY CTA SECTION */}
      <section
        className="relative py-12 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)' }}
      >
        {/* Ambient glow — decorative, no text */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#ea580c] rounded-full blur-3xl animate-pulse" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/40 rounded-full px-4 py-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse" />
            <span className="text-red-300 font-bold text-sm uppercase tracking-widest">
              Emergency Service Available
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black italic uppercase text-white mb-4">
            Sprinkler Emergency?<br />
            <span className="text-[#ea580c]">We Answer The Phone.</span>
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Flooding yard? System won't shut off? Major leak near your foundation? We offer
            emergency-priority after-hours service. Don't wait — water damage gets expensive fast.
          </p>
          <a
            href={`tel:${COMPANY_INFO.phone}`}
            className="inline-flex items-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-black text-2xl px-10 py-5 rounded-2xl shadow-2xl shadow-orange-500/50 hover:shadow-orange-500/70 transition-all duration-300 hover:scale-105 uppercase tracking-wide"
          >
            <Phone className="w-7 h-7" />
            {COMPANY_INFO.phoneFormatted}
          </a>
        </div>
      </section>

      {/* ════════════════════════════════════════════════ SERVICES GRID */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 mb-4">
              <Droplet className="w-4 h-4 text-[#1b7abf]" />
              <span className="text-sm font-bold text-[#1b7abf] uppercase tracking-wide">
                What We Do
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black italic uppercase text-slate-900 mb-4">
              Irrigation Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From a single broken head to full commercial systems — residential and commercial
              irrigation services throughout Collin County.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {featuredServices.map((service) => (
              <Link key={service.id} to={`/services/${service.id}`} className="group block">
                <div className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100 hover:border-[#1b7abf]/30 h-full flex flex-col">
                  {/* Photo — no text overlaid on it */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={SERVICE_PHOTO_MAP[service.id] || '/sprinklers.webp'}
                      alt={`${service.title} — AAA Irrigation Service in Allen, Plano and Frisco TX`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                      width="400"
                      height="208"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  {/* Text content below the photo */}
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-black text-lg text-slate-900 group-hover:text-[#1b7abf] transition-colors mb-2 uppercase tracking-wide">
                      {service.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed flex-1 mb-4">
                      {service.shortDescription}
                    </p>
                    <span className="inline-flex items-center text-[#1b7abf] text-sm font-bold uppercase tracking-wide">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border-2 border-[#1b7abf] text-[#1b7abf] hover:bg-[#1b7abf] hover:text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 uppercase tracking-wide"
            >
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════ WHY AAA GRID */}
      <section className="py-16 md:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black italic uppercase text-white mb-4">
              Why Choose <span className="text-[#1b7abf]">AAA Irrigation?</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              We're not the cheapest option — we're the best value. Here's what sets us apart from
              every other irrigation company in North Texas.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Zap,
                color: '#ea580c',
                title: '1-Year Warranty',
                desc: 'Every repair is covered for a full year on parts and labor. If it fails, we make it right at no charge.',
              },
              {
                icon: Wrench,
                color: '#1b7abf',
                title: 'Fully Stocked Trucks',
                desc: 'Our trucks carry parts for all major brands. We fix it on the first visit — no waiting for parts.',
              },
              {
                icon: Shield,
                color: '#059669',
                title: 'TCEQ Licensed',
                desc: 'License #LI0016993. All techs are state-certified. You know the work is done right and legal.',
                image: PHOTOS.tceqLicense,
              },
              {
                icon: ThumbsUp,
                color: '#ea580c',
                title: 'No Surprises',
                desc: 'Every repair is quoted and digitally signed before we touch anything. The price you agree to is the price you pay.',
              },
              {
                icon: Award,
                color: '#1b7abf',
                title: 'Factory Certified',
                desc: 'Certified on Rachio, Irrigreen, and all major brands. We know these systems inside and out.',
              },
              {
                icon: Clock,
                color: '#059669',
                title: 'Emergency Priority',
                desc: 'After-hours emergency service available. We treat your urgent call like the emergency it is.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-slate-800 border border-slate-700 rounded-2xl p-6 hover:border-slate-500 transition-colors duration-300"
              >
                {item.image ? (
                  <img
                    src={item.image}
                    alt="Texas TCEQ Licensed Irrigator seal"
                    className="w-16 h-16 rounded-xl object-cover mb-4"
                    loading="lazy"
                    width="64"
                    height="64"
                  />
                ) : (
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: item.color + '20' }}
                  >
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                )}
                <h3 className="font-black text-white text-lg uppercase tracking-wide mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════ TESTIMONIALS */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Top Rated Badge */}
            <div className="inline-flex items-center gap-3 bg-[#1b7abf] text-white px-6 py-3 rounded-full shadow-lg mb-6">
              <Award className="w-5 h-5 text-yellow-300" />
              <span className="font-black uppercase tracking-widest text-sm">Top Rated Irrigation Company — North Texas</span>
              <Award className="w-5 h-5 text-yellow-300" />
            </div>
            <div className="flex items-center justify-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-7 h-7 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <h2 className="text-4xl font-black italic uppercase text-slate-900 mb-2">
              What Our Customers Say
            </h2>
            <p className="text-slate-500 font-medium">4.9 Stars · 29 Verified Reviews</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className={`bg-white rounded-2xl p-6 shadow-md border border-slate-100 flex flex-col ${i === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex">
                    {Array.from({ length: t.stars }, (_, s) => (
                      <Star key={s} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  {/* Platform badge */}
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                      t.source === 'Google'
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-red-50 text-red-600'
                    }`}
                  >
                    {t.source === 'Google' ? (
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M20.16 12.594l-4.995 1.09.96 4.274c.135.6-.45 1.085-.99.81L12 16.944l-3.135 1.824c-.54.276-1.125-.21-.99-.81l.96-4.275-4.995-1.09c-.6-.135-.81-.885-.345-1.26l3.75-3.03-1.305-4.155c-.18-.585.39-1.095.945-.84L12 5.934l4.115-2.625c.555-.255 1.125.255.945.84l-1.305 4.155 3.75 3.03c.465.375.255 1.125-.345 1.26z" fill="#FF1A1A"/>
                      </svg>
                    )}
                    {t.source}
                  </span>
                </div>
                <p className="text-slate-700 text-sm leading-relaxed flex-1 mb-5">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="border-t border-slate-100 pt-4 flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#1b7abf] rounded-full flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-slate-400 text-xs">{t.city}, TX · Verified Review</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════ SERVICE AREAS */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black italic uppercase text-slate-900 mb-3">
              Proudly Serving <span className="text-[#1b7abf]">North Texas</span>
            </h2>
            <p className="text-slate-600">
              Professional irrigation services throughout Collin County and surrounding areas.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {SERVICE_AREAS.map((area) => {
              const slug = area.toLowerCase().replace(/\s+/g, '-');
              return (
                <Link
                  key={area}
                  to={`/locations/${slug}`}
                  className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 hover:border-[#1b7abf] hover:bg-blue-50 rounded-full px-5 py-2.5 transition-all duration-200 cursor-pointer"
                >
                  <MapPin className="w-4 h-4 text-[#1b7abf]" />
                  <span className="text-slate-700 font-semibold text-sm">{area}, TX</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════ FAQ */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black italic uppercase text-slate-900 mb-3">
              Common Questions
            </h2>
            <p className="text-slate-600 text-lg">
              Quick answers about irrigation repair in North Texas.
            </p>
          </div>
          <div className="space-y-3">
            {[
              {
                q: 'How much does sprinkler repair cost in Plano, TX?',
                a: 'We offer very competitive pricing with zero surprises. Every repair is fully quoted and agreed upon — with your digital signature — before any work is performed. You know the cost before we start.',
              },
              {
                q: 'Are you TCEQ licensed for irrigation work in Texas?',
                a: 'Yes. TCEQ License #LI0016993. All our technicians are trained and certified under Texas state regulations. You can verify our license directly at the TCEQ website.',
              },
              {
                q: 'How soon can you come out?',
                a: "Book online for any weekday starting tomorrow, and we'll call or text to confirm your appointment within 24 hours. For emergencies, we offer emergency-priority after-hours service to prevent water damage or waste.",
              },
              {
                q: 'What warranty do you offer on repairs?',
                a: 'We stand behind our work with a full 1-year warranty on both parts and labor. If anything we repaired fails within a year, we make it right at no charge.',
              },
              {
                q: 'What brands do you service and are you factory certified?',
                a: 'We service all major brands including Rain Bird, Hunter, Toro, Irritrol, and Weathermatic. We are also factory certified with many brands including Rachio and Irrigreen. Our trucks are fully stocked to get the repair completed on the first visit.',
              },
              {
                q: 'Do you handle backflow preventers for Plano, Allen, and Frisco?',
                a: "Yes — we install backflow preventers throughout Collin County. Once installed, we coordinate directly with a certified backflow tester to get it inspected and compliant. The whole process is typically done within 2 days. One call handles everything.",
              },
            ].map((faq, i) => (
              <details
                key={i}
                className="group bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm"
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer font-bold text-slate-900 hover:bg-slate-50 transition-colors list-none text-base">
                  <span>{faq.q}</span>
                  <span className="text-[#1b7abf] group-open:rotate-45 transition-transform duration-300 flex-shrink-0 ml-4 text-2xl font-thin leading-none">
                    +
                  </span>
                </summary>
                <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════ FINAL CTA */}
      <section className="py-20 bg-[#1b7abf] relative overflow-hidden">
        {/* Decorative blobs — no text */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-white rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black italic uppercase text-white mb-4">
            Ready to Fix Your System?
          </h2>
          <p className="text-blue-100 text-xl mb-8 max-w-2xl mx-auto">
            Join 1,000+ North Texas homeowners and businesses who trust AAA Irrigation Service. Call
            now or request a free estimate online.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={`tel:${COMPANY_INFO.phone}`}
              className="inline-flex items-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-black text-xl px-10 py-5 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 uppercase tracking-wide w-full sm:w-auto justify-center"
            >
              <Phone className="w-6 h-6" />
              {COMPANY_INFO.phoneFormatted}
            </a>
            <Link
              to="/schedule"
              className="inline-flex items-center gap-2 bg-white text-[#1b7abf] hover:bg-blue-50 font-black text-lg px-10 py-5 rounded-2xl transition-all duration-300 uppercase tracking-wide w-full sm:w-auto justify-center"
            >
              <Calendar className="w-5 h-5" />
              Schedule Service
            </Link>
          </div>
          <div className="mt-8 flex items-center justify-center gap-2 text-white">
            <Shield className="w-4 h-4" />
            <a
              href={COMPANY_INFO.licenseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm hover:text-white transition-colors"
            >
              TCEQ Licensed: {COMPANY_INFO.license} &middot; Integrity By The Drop Since 2005
            </a>
          </div>
        </div>
      </section>

    </Layout>
  );
}
